/**
 * LLMs.txt generations list page.
 * 
 * @module pages/LLMsTxtPage
 * @see CSDEV-144 - Content/FAQ/LLMs pages: add business filter UI
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { DataTable, Pagination, Card, Badge } from '../components/ui';
import { useLLMsTxt, usePagination, useAuditLog, useAllBusinesses, useExclusionRules } from '../hooks';
import { isExcludedUser } from '../config/excluded-users';
import { AuditActionTypes } from '../lib/audit';
import { formatDate, truncate } from '../lib/utils';
import type { LLMsTxtGenerationWithBusiness, DataTableColumn } from '../types';

/**
 * LLMs.txt content viewer component.
 */
function LLMsTxtViewer({ item }: { item: LLMsTxtGenerationWithBusiness }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (item.llms_txt_content) {
      await navigator.clipboard.writeText(item.llms_txt_content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex-1">
          <p className="font-medium text-gray-900">
            {item.businesses?.business_name || 'Unknown Business'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {item.recommendations?.length || 0} recommendations • {formatDate(item.created_at)}
          </p>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {isExpanded && (
        <div className="p-4 border-t border-gray-200 space-y-4">
          {/* LLMs.txt content */}
          {item.llms_txt_content && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">LLMs.txt Content</h4>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <pre className="text-sm bg-gray-50 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap max-h-96 overflow-y-auto">
                {item.llms_txt_content}
              </pre>
            </div>
          )}
          {/* Recommendations */}
          {item.recommendations && item.recommendations.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Recommendations</h4>
              <ul className="space-y-2">
                {item.recommendations.map((rec, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm text-gray-600"
                  >
                    <span className="text-primary-500 mt-1">•</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * LLMs.txt generations list page component.
 * 
 * @returns LLMs.txt page element
 */
export function LLMsTxtPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, pageSize, goToPage, changePageSize, pageSizeOptions } = usePagination();
  const { logAction } = useAuditLog();
  const [internalFilter, setInternalFilter] = useState<'all' | 'external' | 'internal'>('external');

  // Get business filter from URL
  const businessFilter = searchParams.get('business') || '';

  // Fetch filter options
  const { data: businesses } = useAllBusinesses();
  const { data: exclusionRules, isLoading: exclusionRulesLoading } = useExclusionRules();

  const isFilteringByInternal = internalFilter !== 'all';

  const { data, isLoading, error } = useLLMsTxt({
    page: isFilteringByInternal ? 1 : page,
    pageSize: isFilteringByInternal ? 10000 : pageSize,
    businessId: businessFilter || undefined,
  });

  const filteredData = data?.data.filter((item) => {
    if (internalFilter === 'all') return true;
    const excluded = isExcludedUser(item.businesses?.users?.email, exclusionRules);
    return internalFilter === 'internal' ? excluded : !excluded;
  });

  /**
   * Updates URL search params for filters.
   */
  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
    goToPage(1);
  };

  // Log page view
  useEffect(() => {
    logAction(AuditActionTypes.VIEW_LLMS_TXT_LIST);
  }, [logAction]);

  const columns: DataTableColumn<LLMsTxtGenerationWithBusiness>[] = [
    {
      key: 'businesses',
      header: 'Business',
      render: (item) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (item.businesses?.id) {
              navigate(`/businesses/${item.businesses.id}`);
            }
          }}
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          {item.businesses?.business_name || '—'}
        </button>
      ),
    },
    {
      key: 'llms_txt_content',
      header: 'Content Preview',
      render: (item) => (
        <span className="text-gray-500 text-sm">
          {truncate(item.llms_txt_content || '', 60)}
        </span>
      ),
    },
    {
      key: 'recommendations',
      header: 'Recommendations',
      align: 'center',
      render: (item) => (
        <Badge variant="info" size="sm">
          {item.recommendations?.length || 0}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: 'Date',
      sortable: true,
      render: (item) => (
        <span className="text-gray-500">{formatDate(item.created_at)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900">LLMs.txt Generations</h1>
        <p className="text-gray-500">
          View generated LLMs.txt files and recommendations
        </p>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Business filter */}
          <select
            value={businessFilter}
            onChange={(e) => updateFilter('business', e.target.value)}
            className="input w-full sm:w-64"
          >
            <option value="">All businesses</option>
            {businesses?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.business_name}
              </option>
            ))}
          </select>

          {/* Internal/External filter */}
          <select
            value={internalFilter}
            onChange={(e) => {
              setInternalFilter(e.target.value as 'all' | 'external' | 'internal');
              goToPage(1);
            }}
            disabled={exclusionRulesLoading}
            className="input w-full sm:w-48"
          >
            <option value="all">All results</option>
            <option value="external">External only</option>
            <option value="internal">Internal only</option>
          </select>

          {/* Clear filters button */}
          {(businessFilter || internalFilter !== 'external') && (
            <button
              onClick={() => {
                setSearchParams({});
                setInternalFilter('external');
              }}
              className="btn-secondary text-sm"
            >
              Clear filter
            </button>
          )}
        </div>
      </Card>

      {/* Error state */}
      {error && (
        <Card>
          <p className="text-error-600">Failed to load LLMs.txt generations: {error.message}</p>
        </Card>
      )}

      {/* Data table */}
      <Card padding="none">
        <DataTable
          columns={columns}
          data={filteredData || []}
          isLoading={isLoading}
          emptyMessage="No LLMs.txt generations found"
          keyExtractor={(item) => item.id}
        />
        {data && isFilteringByInternal ? (
          <div className="px-4 py-3 text-sm text-gray-500 border-t border-gray-200">
            {filteredData?.length ?? 0} {internalFilter} result{(filteredData?.length ?? 0) !== 1 ? 's' : ''} found
          </div>
        ) : data && data.totalPages > 0 ? (
          <Pagination
            currentPage={page}
            totalPages={data.totalPages}
            onPageChange={goToPage}
            pageSize={pageSize}
            onPageSizeChange={changePageSize}
            totalItems={data.count}
            pageSizeOptions={pageSizeOptions}
          />
        ) : null}
      </Card>

      {/* Detailed view */}
      {filteredData && filteredData.length > 0 && (
        <Card title="Detailed View">
          <div className="space-y-3">
            {filteredData.slice(0, 5).map((item) => (
              <LLMsTxtViewer key={item.id} item={item} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
